import { useFloatNotification } from 'contexts/FloatNotificationContext';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LatLng, PolygonSchema, Polygons } from 'types/maps';

type PolygonPaths = google.maps.PolygonOptions['paths'];

interface PolygonItem {
  paths: PolygonPaths;
  options?: Omit<google.maps.PolygonOptions, 'paths'>;
}

type VertexDeletionMode = 'both' | 'doubleClick' | 'rightClick' | 'none';

type CenterEventName = 'idle' | 'center_changed' | 'dragend';

interface DarwinGoogleMapsProps {
  height?: string | number;
  width?: string | number;
  disableUi?: boolean | null | undefined;
  mapType?: google.maps.MapTypeId | string;
  center: LatLng;
  zoom?: number;
  zoomControl?: boolean | null | undefined;
  zoomControlOptions?: google.maps.ZoomControlOptions;
  polygons?: PolygonItem[];
  polygonIds?: string[];
  fitToPolygons?: boolean;
  fitToPolygonsOnce?: boolean;
  onPolygonsChange?: (polys: Polygons) => void;
  activeId?: string | null;
  onPolygonClick?: (id: string) => void;
  vertexDeletionMode?: VertexDeletionMode;
  onCenterChange?: (center: LatLng) => void;
  centerChangeEvent?: CenterEventName;
  centerChangeDebounceMs?: number;
  emitInitialCenter?: boolean;
}

function latLngToLiteral(ll: google.maps.LatLng): LatLng {
  return { lat: ll.lat(), lng: ll.lng() };
}

function pathToRing(
  path: google.maps.MVCArray<google.maps.LatLng>
): PolygonSchema {
  const out: PolygonSchema = [];
  for (let i = 0; i < path.getLength(); i++)
    out.push(latLngToLiteral(path.getAt(i)));
  return out;
}

const MINIMUM_VERTICES = 3;

const DarwinGoogleMap: React.FC<DarwinGoogleMapsProps> = ({
  height = 400,
  width = '100%',
  disableUi,
  mapType,
  zoom = 8,
  zoomControl,
  zoomControlOptions,
  center,
  polygons,
  polygonIds,
  fitToPolygons = false,
  fitToPolygonsOnce = false,
  onPolygonsChange,
  activeId,
  onPolygonClick,
  vertexDeletionMode = 'none',
  onCenterChange,
  centerChangeEvent = 'idle',
  centerChangeDebounceMs = 120,
  emitInitialCenter = false,
}) => {
  const mapDivRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const polygonRefs = useRef<google.maps.Polygon[]>([]);
  const listenerRefs = useRef<google.maps.MapsEventListener[][]>([]);
  const lastPayloadRef = useRef<string>('');
  const onChangeRef = useRef<typeof onPolygonsChange>();
  const [isMapReady, setIsMapReady] = useState(false);
  const { openNotification } = useFloatNotification();
  const { t } = useTranslation();

  const lastEmittedCenterKeyRef = useRef<string>('');
  const suppressNextCenterEmitRef = useRef<boolean>(!emitInitialCenter);
  const centerListenerRef = useRef<google.maps.MapsEventListener | null>(null);
  const centerDebounceTimerRef = useRef<number | undefined>(undefined);
  const prevCenterPropRef = useRef<LatLng>(center);
  const prevZoomPropRef = useRef<number | undefined>(zoom);
  const onCenterChangeRef = useRef<typeof onCenterChange>();

  const didInitialFitRef = useRef<boolean>(false);

  const centerKey = (c: LatLng) => `${c.lat.toFixed(6)},${c.lng.toFixed(6)}`;
  const centersEqual = (a: LatLng, b: LatLng) =>
    a.lat === b.lat && a.lng === b.lng;

  const emitCenterNow = () => {
    const map = mapRef.current;
    if (!map || !onCenterChangeRef.current) return;
    const c = map.getCenter();
    if (!c) return;
    const plain: LatLng = { lat: c.lat(), lng: c.lng() };
    const k = centerKey(plain);
    if (k === lastEmittedCenterKeyRef.current) return;
    lastEmittedCenterKeyRef.current = k;
    onCenterChangeRef.current(plain);
  };

  useEffect(() => {
    onChangeRef.current = onPolygonsChange;
  }, [onPolygonsChange]);

  useEffect(() => {
    onCenterChangeRef.current = onCenterChange;
  }, [onCenterChange]);

  useEffect(() => {
    (async () => {
      const { Map } = (await google.maps.importLibrary(
        'maps'
      )) as google.maps.MapsLibrary;
      mapRef.current = new Map(mapDivRef.current as HTMLElement, {
        center,
        zoom,
        disableDefaultUI: disableUi ?? undefined,
        mapTypeId: mapType as any,
        zoomControl,
        zoomControlOptions,
      });
      setIsMapReady(true);
    })();

    return () => {
      if (centerListenerRef.current) {
        centerListenerRef.current.remove();
        centerListenerRef.current = null;
      }
      listenerRefs.current.forEach(arr => arr.forEach(l => l.remove()));
      listenerRefs.current = [];
      polygonRefs.current.forEach(p => p.setMap(null));
      polygonRefs.current = [];
      mapRef.current = null;
      window.clearTimeout(centerDebounceTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isMapReady || !mapRef.current || !onCenterChange) return;

    if (centerListenerRef.current) {
      centerListenerRef.current.remove();
      centerListenerRef.current = null;
    }

    const map = mapRef.current;
    const emit = () => {
      if (!map) return;
      const c = map.getCenter();
      if (!c) return;
      const plain: LatLng = { lat: c.lat(), lng: c.lng() };
      if (suppressNextCenterEmitRef.current) {
        suppressNextCenterEmitRef.current = false;
        return;
      }
      const k = centerKey(plain);
      if (k === lastEmittedCenterKeyRef.current) return;
      lastEmittedCenterKeyRef.current = k;
      onCenterChange(plain);
    };

    const handler = () => {
      window.clearTimeout(centerDebounceTimerRef.current);
      centerDebounceTimerRef.current = window.setTimeout(
        emit,
        Math.max(0, centerChangeDebounceMs)
      );
    };

    centerListenerRef.current = map.addListener(centerChangeEvent, handler);

    return () => {
      if (centerListenerRef.current) {
        centerListenerRef.current.remove();
        centerListenerRef.current = null;
      }
      window.clearTimeout(centerDebounceTimerRef.current);
    };
  }, [isMapReady, onCenterChange, centerChangeEvent, centerChangeDebounceMs]);

  useEffect(() => {
    if (!isMapReady) return;
    const map = mapRef.current;
    if (!map) return;

    const centerChangedFromProp = !centersEqual(
      prevCenterPropRef.current,
      center
    );
    const zoomChangedFromProp = prevZoomPropRef.current !== zoom;
    if (centerChangedFromProp) {
      suppressNextCenterEmitRef.current = true;
    }

    const opts: google.maps.MapOptions = {
      disableDefaultUI: disableUi ?? undefined,
      mapTypeId: mapType as any,
      zoomControl,
      zoomControlOptions,
    };
    if (centerChangedFromProp) {
      opts.center = center;
    }
    if (zoomChangedFromProp && typeof zoom === 'number') {
      opts.zoom = zoom;
    }
    map.setOptions(opts);

    prevCenterPropRef.current = center;
    prevZoomPropRef.current = zoom;
  }, [
    isMapReady,
    center,
    zoom,
    disableUi,
    mapType,
    zoomControl,
    zoomControlOptions,
  ]);

  useEffect(() => {
    if (!isMapReady) return;
    const map = mapRef.current!;
    if (!polygons) return;

    const DEFAULT_FLAGS: google.maps.PolygonOptions = {
      editable: false,
      draggable: false,
      clickable: true,
    };

    const optionsEqual = (
      a?: Omit<google.maps.PolygonOptions, 'paths'>,
      b?: Omit<google.maps.PolygonOptions, 'paths'>
    ) => JSON.stringify(a ?? {}) === JSON.stringify(b ?? {});

    if (polygonRefs.current.length === 0) {
      polygons.forEach(({ paths, options }, i) => {
        const poly = new google.maps.Polygon({
          paths,
          ...DEFAULT_FLAGS,
          ...options,
        });
        poly.setMap(map);
        polygonRefs.current[i] = poly;
      });
    } else {
      if (polygonRefs.current.length !== polygons.length) {
        listenerRefs.current.forEach(arr => arr.forEach(l => l.remove()));
        listenerRefs.current = [];
        polygonRefs.current.forEach(p => p.setMap(null));
        polygonRefs.current = [];

        polygons.forEach(({ paths, options }, i) => {
          const poly = new google.maps.Polygon({
            paths,
            ...DEFAULT_FLAGS,
            ...options,
          });
          poly.setMap(map);
          polygonRefs.current[i] = poly;
        });
      } else {
        polygons.forEach(({ options }, i) => {
          const poly = polygonRefs.current[i];
          if (!optionsEqual(poly.get('optionsCache'), options)) {
            poly.setOptions({
              ...DEFAULT_FLAGS,
              ...options,
              paths: poly.getPath(),
            });
            poly.set('optionsCache', options ?? {});
          }
        });
      }
    }

    listenerRefs.current.forEach(arr => arr.forEach(l => l.remove()));
    listenerRefs.current = [];

    let timeout: number | undefined;
    const emitAllDebounced = () => {
      window.clearTimeout(timeout);
      timeout = window.setTimeout(() => {
        const plain: Polygons = polygonRefs.current.map(poly => ({
          paths: pathToRing(poly.getPath()),
        }));
        const json = JSON.stringify(plain);
        if (json === lastPayloadRef.current) return;
        lastPayloadRef.current = json;
        onChangeRef.current?.(plain);
      }, 80);
    };

    const handleVertexDeletion = (
      poly: google.maps.Polygon,
      vertexIndex: number
    ) => {
      const path = poly.getPath();
      if (path.getLength() <= MINIMUM_VERTICES) {
        openNotification(
          t('info'),
          t('geolocation.modal.min-vertex-info'),
          'info'
        );
        return;
      }
      path.removeAt(vertexIndex);
      emitAllDebounced();
    };

    polygonRefs.current.forEach((poly, idx) => {
      const path = poly.getPath();
      const perPoly: google.maps.MapsEventListener[] = [
        path.addListener('insert_at', emitAllDebounced),
        path.addListener('remove_at', emitAllDebounced),
        path.addListener('set_at', emitAllDebounced),
        google.maps.event.addListener(poly, 'dragend', emitAllDebounced),
        google.maps.event.addListener(poly, 'mouseup', emitAllDebounced),
      ];

      if (onPolygonClick && polygonIds && polygonIds[idx] != null) {
        perPoly.push(
          google.maps.event.addListener(poly, 'click', () => {
            onPolygonClick(polygonIds[idx]!);
          })
        );
      }

      const isActivePolygon = activeId && polygonIds?.[idx] === activeId;
      if (
        isActivePolygon &&
        vertexDeletionMode !== 'none' &&
        (vertexDeletionMode === 'rightClick' || vertexDeletionMode === 'both')
      ) {
        perPoly.push(
          google.maps.event.addListener(
            poly,
            'rightclick',
            (event: google.maps.PolyMouseEvent) => {
              if (event.vertex !== undefined) {
                event.stop?.();
                handleVertexDeletion(poly, event.vertex);
              }
            }
          )
        );
      }

      if (
        isActivePolygon &&
        vertexDeletionMode !== 'none' &&
        (vertexDeletionMode === 'doubleClick' || vertexDeletionMode === 'both')
      ) {
        perPoly.push(
          google.maps.event.addListener(
            poly,
            'dblclick',
            (event: google.maps.PolyMouseEvent) => {
              if (event.vertex !== undefined) {
                event.stop?.();
                handleVertexDeletion(poly, event.vertex);
              }
            }
          )
        );
      }

      listenerRefs.current[idx] = perPoly;
    });

    {
      const bounds = new google.maps.LatLngBounds();
      polygonRefs.current.forEach(poly => {
        const path = poly.getPath();
        for (let i = 0; i < path.getLength(); i++) bounds.extend(path.getAt(i));
      });
      if (!bounds.isEmpty()) {
        let didFit = false;
        if (fitToPolygonsOnce) {
          if (!didInitialFitRef.current) {
            map.fitBounds(bounds);
            didInitialFitRef.current = true;
            didFit = true;
          }
        } else if (fitToPolygons) {
          map.fitBounds(bounds);
          didFit = true;
        }
        if (didFit && onCenterChangeRef.current) {
          google.maps.event.addListenerOnce(map, 'idle', () => {
            emitCenterNow();
          });
        }
      }
    }

    return () => {
      listenerRefs.current.forEach(arr => arr.forEach(l => l.remove()));
      listenerRefs.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isMapReady,
    polygons,
    fitToPolygons,
    fitToPolygonsOnce,
    onPolygonClick,
    polygonIds,
    activeId,
    vertexDeletionMode,
    t,
    openNotification,
  ]);

  useEffect(() => {
    if (!polygonRefs.current.length) return;

    polygonRefs.current.forEach((poly, idx) => {
      const id = polygonIds?.[idx];
      const shouldBeActive = !!activeId && id === activeId;

      const curEditable =
        !!(poly as any).getEditable?.() || !!poly.get('editable');
      const curDraggable =
        !!(poly as any).getDraggable?.() || !!poly.get('draggable');

      if (curEditable !== shouldBeActive || curDraggable !== shouldBeActive) {
        poly.setOptions({
          editable: shouldBeActive,
          draggable: shouldBeActive,
        });
      }
    });
  }, [activeId, polygonIds]);

  return (
    <div
      ref={mapDivRef}
      id="map"
      style={{ width, height, borderRadius: '8px' }}
    />
  );
};

export default DarwinGoogleMap;
